import type { MeResponse } from "@keydials/api-client";

/**
 * The client-side rules of a kiosk session (KEY-268), in one place so the router and the header cannot
 * drift apart about what a PIN-unlocked session may reach.
 *
 * None of this is a security boundary — the server confines the token on every request regardless. Its job
 * is to stop the UI offering doors it knows are locked, which would otherwise show a carer a "My Dials"
 * button that only ever produces a 403.
 */
export interface KioskNav {
  /** True when this session is confined to one dial — an unlock-PIN session or a guest session. */
  locked: boolean;
  /** Which kind of confined session this is; null for an ordinary one. A guest (KEY-297) gets the same
   * single-dial routing as a PIN session plus its own banner and sign-out-only header. */
  kind: "pin" | "guest" | null;
  /** Where the logo goes, or null when there is nowhere to go — a confined session has no home. */
  homePath: string | null;
  /** The only workspace route a confined session may mount. Null for an ordinary session (all are allowed). */
  instancePath: string | null;
  /** The id behind `instancePath`. The route is a LITERAL path so another dial's URL can never match it,
   * which means `useParams()` yields nothing on it — so the workspace has to be handed its id directly.
   * Forgetting that is what left a confined session on "Loading…" forever: every loader bails without an id. */
  instanceId: string | null;
}

export function kioskNav(me: Pick<MeResponse, "pinnedToInstanceId" | "guestInstanceId"> | null | undefined): KioskNav {
  const pinned = me?.pinnedToInstanceId ?? null;
  if (pinned) {
    return { locked: true, kind: "pin", homePath: null, instancePath: `/instances/${pinned}`, instanceId: pinned };
  }
  const guest = me?.guestInstanceId ?? null;
  if (guest) {
    return { locked: true, kind: "guest", homePath: null, instancePath: `/instances/${guest}`, instanceId: guest };
  }
  return { locked: false, kind: null, homePath: "/instances", instancePath: null, instanceId: null };
}

/**
 * Whether a shared device has been idle long enough to lock.
 *
 * Expressed against wall-clock timestamps rather than an elapsed-timer chain on purpose: a wall-mounted
 * tablet is left alone rather than switched off, and a backgrounded tab has its timers throttled or frozen.
 * A device woken after two hours must be found idle immediately, not handed a fresh countdown.
 */
export function hasGoneIdle(lastActivityMs: number, nowMs: number, timeoutSeconds: number): boolean {
  if (timeoutSeconds <= 0) {
    return false;
  }
  return nowMs - lastActivityMs >= timeoutSeconds * 1000;
}
